import { useSyncExternalStore } from 'react'

/**
 * Viewport Segments (W3C CSS Viewport Segments + Device Posture) per i dispositivi pieghevoli.
 * Sorgenti, in ordine: simulazione (showcase e test) > `window.viewport.segments` (Chrome >= 138) > un solo segmento.
 * Le media query `(horizontal-viewport-segments: 2)`, `(vertical-viewport-segments: 2)` e `(device-posture: folded)`
 * servono solo come segnale di cambiamento e per la postura.
 *
 * "horizontal" = segmenti affiancati (la piega e' verticale); "vertical" = segmenti uno sopra l'altro (tabletop).
 */
export type SegmentAxis = 'none' | 'horizontal' | 'vertical'
export interface SegmentRect { left: number; top: number; width: number; height: number }
export interface ViewportSegments {
  axis: SegmentAxis
  /** 1 o 2 segmenti, in px CSS del viewport. */
  segments: SegmentRect[]
  /** Area tra i segmenti; width/height 0 se la piega non occlude. */
  hinge: SegmentRect | null
  /** Cerniera con spessore > 0 oppure postura "folded". */
  separating: boolean
}

const SINGLE_FALLBACK: ViewportSegments = {
  axis: 'none', segments: [{ left: 0, top: 0, width: 0, height: 0 }], hinge: null, separating: false,
}

const QUERIES = ['(horizontal-viewport-segments: 2)', '(vertical-viewport-segments: 2)', '(device-posture: folded)'] as const

interface Simulation { axis: 'h' | 'v'; offset: number; mask: number; folded: boolean }
let simulation: Simulation | null = null
const listeners = new Set<() => void>()
let cache: ViewportSegments | null = null

function parseSpec(spec: string): Simulation | null {
  const [a, off, mask = '0', posture] = spec.split(':')
  const offset = Number(off)
  const m = Number(mask)
  if ((a !== 'h' && a !== 'v') || !Number.isFinite(offset) || !Number.isFinite(m)) return null
  return { axis: a, offset, mask: m, folded: posture === 'folded' }
}

function postureFolded(): boolean {
  try { return window.matchMedia('(device-posture: folded)').matches } catch { return false }
}

function fromRects(rects: SegmentRect[], folded: boolean): ViewportSegments {
  if (rects.length < 2) {
    const r = rects[0] ?? { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight }
    return { axis: 'none', segments: [r], hinge: null, separating: folded }
  }
  const a = rects[0] as SegmentRect
  const b = rects[1] as SegmentRect
  const side = b.left >= a.left + a.width - 0.5 && Math.abs(b.top - a.top) < 1
  if (side) {
    const size = Math.max(0, b.left - (a.left + a.width))
    return { axis: 'horizontal', segments: [a, b], hinge: { left: a.left + a.width, top: a.top, width: size, height: a.height }, separating: size > 0 || folded }
  }
  const size = Math.max(0, b.top - (a.top + a.height))
  return { axis: 'vertical', segments: [a, b], hinge: { left: a.left, top: a.top + a.height, width: a.width, height: size }, separating: size > 0 || folded }
}

function compute(): ViewportSegments {
  if (typeof window === 'undefined') return SINGLE_FALLBACK
  const W = window.innerWidth
  const H = window.innerHeight
  if (simulation) {
    const s = simulation
    const rects: SegmentRect[] = s.axis === 'h'
      ? [{ left: 0, top: 0, width: s.offset, height: H }, { left: s.offset + s.mask, top: 0, width: Math.max(0, W - s.offset - s.mask), height: H }]
      : [{ left: 0, top: 0, width: W, height: s.offset }, { left: 0, top: s.offset + s.mask, width: W, height: Math.max(0, H - s.offset - s.mask) }]
    return fromRects(rects, s.folded)
  }
  const raw = (window as unknown as { viewport?: { segments?: ArrayLike<{ x?: number; y?: number; left?: number; top?: number; width: number; height: number }> | null } }).viewport?.segments
  if (raw && raw.length >= 2) {
    const rects = Array.from(raw).slice(0, 2).map(r => ({ left: r.x ?? r.left ?? 0, top: r.y ?? r.top ?? 0, width: r.width, height: r.height }))
    return fromRects(rects, postureFolded())
  }
  return fromRects([{ left: 0, top: 0, width: W, height: H }], postureFolded())
}

function same(a: ViewportSegments, b: ViewportSegments): boolean {
  if (a.axis !== b.axis || a.separating !== b.separating || a.segments.length !== b.segments.length) return false
  const eq = (x: SegmentRect, y: SegmentRect) => x.left === y.left && x.top === y.top && x.width === y.width && x.height === y.height
  return a.segments.every((s, i) => eq(s, b.segments[i] as SegmentRect)) && (a.hinge === null ? b.hinge === null : b.hinge !== null && eq(a.hinge, b.hinge))
}

/** Istantanea stabile (stessa referenza finche' nulla cambia): adatta a useSyncExternalStore. */
export function getViewportSegments(): ViewportSegments {
  const next = compute()
  if (cache && same(cache, next)) return cache
  cache = next
  return next
}

export function subscribeViewportSegments(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined
  listeners.add(cb)
  const lists = QUERIES.map(q => window.matchMedia(q))
  lists.forEach(l => l.addEventListener('change', cb))
  window.addEventListener('resize', cb)
  const vp = (window as unknown as { viewport?: EventTarget }).viewport
  vp?.addEventListener?.('segmentchange', cb)
  const dp = (navigator as unknown as { devicePosture?: EventTarget }).devicePosture
  dp?.addEventListener?.('change', cb)
  return () => {
    listeners.delete(cb)
    lists.forEach(l => l.removeEventListener('change', cb))
    window.removeEventListener('resize', cb)
    vp?.removeEventListener?.('segmentchange', cb)
    dp?.removeEventListener?.('change', cb)
  }
}

export function useViewportSegments(): ViewportSegments {
  return useSyncExternalStore(subscribeViewportSegments, getViewportSegments, () => SINGLE_FALLBACK)
}

/** Solo showcase e test: "h:<offset>:<mask>[:folded]" | "v:<offset>:<mask>[:folded]" | null. */
export function simulateViewportSegments(spec: string | null): void {
  simulation = spec ? parseSpec(spec) : null
  cache = null
  listeners.forEach(l => l())
}

/** Segmento che contiene il centro di `r` (il primo se nessuno lo contiene). */
export function segmentFor(r: { left: number; top: number; width: number; height: number }, vs: ViewportSegments = getViewportSegments()): SegmentRect {
  const cx = r.left + r.width / 2
  const cy = r.top + r.height / 2
  const hit = vs.segments.find(s => cx >= s.left && cx <= s.left + s.width && cy >= s.top && cy <= s.top + s.height)
  return hit ?? (vs.segments[0] as SegmentRect)
}

/** Limiti utili `{left,top,right,bottom}` per posizionare popup ancorati: il segmento dell'ancora, o il viewport. */
export function boundsFor(r: { left: number; top: number; width: number; height: number }): { left: number; top: number; right: number; bottom: number } {
  const vs = getViewportSegments()
  if (vs.axis === 'none') return { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight }
  const s = segmentFor(r, vs)
  return { left: s.left, top: s.top, right: s.left + s.width, bottom: s.top + s.height }
}

const px = (n: number) => `${Math.round(n * 100) / 100}px`

/**
 * Scrive su <html>: data-segments="h"|"v" (assente con 1 segmento), data-separating="true|false",
 * data-seg-compact="true" se il segmento di destinazione dei dialog e' < 600, e le variabili
 * --md-seg0-left|top|width|height, --md-seg1-..., --md-hinge-start, --md-hinge-size (px).
 * Con un solo segmento rimuove le variabili: restano i valori predefiniti di tokens.css. Ritorna il cleanup.
 */
export function syncSegmentAttributes(): () => void {
  const root = document.documentElement
  const VARS = ['seg0-left', 'seg0-top', 'seg0-width', 'seg0-height', 'seg1-left', 'seg1-top', 'seg1-width', 'seg1-height', 'hinge-start', 'hinge-size']
  const clear = () => {
    VARS.forEach(v => root.style.removeProperty(`--md-${v}`))
    root.removeAttribute('data-segments')
    root.removeAttribute('data-separating')
    root.removeAttribute('data-seg-compact')
  }
  const apply = () => {
    const vs = getViewportSegments()
    if (vs.axis === 'none') {
      clear()
      root.setAttribute('data-separating', String(vs.separating))
      // Un solo segmento: la destinazione dei dialog e' il viewport.
      root.setAttribute('data-seg-compact', String(window.innerWidth < 600))
      return
    }
    const [s0, s1] = vs.segments as [SegmentRect, SegmentRect]
    const h = vs.hinge as SegmentRect
    root.setAttribute('data-segments', vs.axis === 'horizontal' ? 'h' : 'v')
    root.setAttribute('data-separating', String(vs.separating))
    const dest = vs.axis === 'horizontal' ? s1 : s0
    root.setAttribute('data-seg-compact', String(dest.width < 600))
    const set = (k: string, v: number) => root.style.setProperty(`--md-${k}`, px(v))
    set('seg0-left', s0.left); set('seg0-top', s0.top); set('seg0-width', s0.width); set('seg0-height', s0.height)
    set('seg1-left', s1.left); set('seg1-top', s1.top); set('seg1-width', s1.width); set('seg1-height', s1.height)
    set('hinge-start', vs.axis === 'horizontal' ? h.left : h.top)
    set('hinge-size', vs.axis === 'horizontal' ? h.width : h.height)
  }
  apply()
  const un = subscribeViewportSegments(apply)
  return () => { un(); clear() }
}
